import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Search, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ApiService from './ApiService';

const ACTION_LABELS = {
  PATIENT_CREATED: 'Patient created',
  PATIENT_UPDATED: 'Patient edited',
  PATIENT_DELETED: 'Patient deleted',
  MEDICATION_ASSIGNED: 'Medication assigned',
  MEDICATION_REMOVED: 'Medication removed',
  PROTOCOL_ASSIGNED: 'Protocol assigned',
  PROTOCOL_UPDATED: 'Protocol updated',
  PROTOCOL_REMOVED: 'Protocol removed',
  SERVICE_ASSIGNED: 'Service scheduled',
  SERVICE_REMOVED: 'Service removed',
  NOTE_CREATED: 'Note added',
  NOTE_UPDATED: 'Note edited',
  NOTE_DELETED: 'Note deleted',
  PATIENTS_MERGED: 'Records merged',
  DUPLICATE_DISMISSED: 'Duplicate dismissed',
  BULK_IMPORT_STARTED: 'Bulk import',
};

const toneFor = (action) =>
  action.includes('DELETED') || action.includes('REMOVED') ? 'text-danger border-danger'
  : action.includes('MERGED') ? 'text-copper-600 border-copper-600'
  : action.includes('CREATED') || action.includes('ASSIGNED') ? 'text-pos border-pos'
  : 'text-spruce-600 border-spruce-600';

const ActivityLogPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ApiService.getActivityLog(days, 200);
      setEntries(data.entries || []);
      setError('');
    } catch (err) {
      setError('Could not load activity: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? entries.filter(e =>
        (e.patientName || '').toLowerCase().includes(q) ||
        (e.performedByEmail || e.performedBy || '').toLowerCase().includes(q) ||
        (ACTION_LABELS[e.action] || e.action).toLowerCase().includes(q))
    : entries;

  return (
    <div className="min-h-screen bg-porcelain py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
              <Activity className="text-copper-500" size={24} /> Activity
            </h1>
            <p className="text-sm text-ink-soft mt-1">Every action taken in the portal, most recent first.</p>
          </div>
          <button onClick={load} className="flex items-center gap-2 border border-line bg-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-mist">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by patient, user, or action..."
              className="w-full pl-9 pr-4 py-2 border border-line rounded-lg bg-white focus:ring-2 focus:ring-spruce-500 focus:border-transparent text-sm"
            />
          </div>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="border border-line rounded-lg px-3 py-2 bg-white text-sm"
          >
            <option value={1}>Today</option>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={31}>Last 31 days</option>
          </select>
        </div>

        {error && <div className="text-danger text-sm mb-4">{error}</div>}

        <div className="bg-white border border-line rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-mist text-left">
                <th className="px-4 py-3 font-data text-[10.5px] uppercase tracking-wider text-ink-soft">When</th>
                <th className="px-4 py-3 font-data text-[10.5px] uppercase tracking-wider text-ink-soft">Action</th>
                <th className="px-4 py-3 font-data text-[10.5px] uppercase tracking-wider text-ink-soft">Patient</th>
                <th className="px-4 py-3 font-data text-[10.5px] uppercase tracking-wider text-ink-soft">By</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-500">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-500">No activity found.</td></tr>
              ) : filtered.map((e) => {
                const when = new Date(e.timestamp);
                return (
                  <tr key={e.auditId} className="border-t border-line hover:bg-porcelain">
                    <td className="px-4 py-3 font-data text-xs text-ink-soft whitespace-nowrap">
                      {when.toLocaleDateString()} {when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block border rounded-full px-2.5 py-1 text-[11px] font-data font-semibold ${toneFor(e.action)}`}>
                        {ACTION_LABELS[e.action] || e.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {e.patientId ? (
                        <button
                          onClick={() => navigate(`/patients/${e.patientId}`)}
                          className="text-spruce-600 hover:text-spruce-800 hover:underline font-medium"
                        >
                          {e.patientName || e.patientId.slice(0, 8)}
                        </button>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-ink-soft">{e.performedByEmail || e.performedBy}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogPage;
